import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useCredits } from '../context/CreditContext';
import { IMG_MODELS, RATIOS } from '../constants/models';
import * as api from '../services/api';
import Icons from '../components/Icons';
import toast from 'react-hot-toast';
import { compressImage } from '../utils/imageCompression';
import { downloadFile } from '../utils/downloadFile';
import MediaLibraryModal from '../components/MediaLibraryModal';
import { confirmAlert } from '../utils/confirmAlert';
import AspectRatioSelector from '../components/AspectRatioSelector';

const UPSCALE_RES = [
  { id: '1k', label: '1K', desc: '1024px', cost: 2 },
  { id: '2k', label: '2K', desc: '2048px', cost: 3 },
  { id: '4k', label: '4K', desc: '4096px', cost: 5 },
];

const IMG_RES = [
  { id: '1k', label: '1K', desc: '1024px', mult: 1 },
  { id: '2k', label: '2K', desc: '2048px', mult: 1.5 },
];

export default function EditPage({ onShowAuth }) {
  const { user, token } = useAuth();
  const { credits, setCredits, refreshItems, pendingJobs, setPendingJobs } = useCredits();
  const navigate = useNavigate();
  const location = useLocation();

  const incoming = location.state || {};
  const [mode, setMode] = useState(incoming.mode || 'recreate');
  const [sourceUrl, setSourceUrl] = useState(incoming.sourceUrl || null);
  const [prompt, setPrompt] = useState(incoming.prompt || '');
  const [showMobilePreview, setShowMobilePreview] = useState(false);
  const [model, setModel] = useState('gemini-2.5-flash');
  const [sourceImg, setSourceImg] = useState(null); // File object or from library
  const [libOpen, setLibOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState([]);
  const [fullscreen, setFullscreen] = useState(false);
  const [fsIdx, setFsIdx] = useState(0);
  const [cd, setCd] = useState(0);
  const [showModelPicker, setShowModelPicker] = useState(false);

  // Upscale options
  const [upscaleRes, setUpscaleRes] = useState('2k');
  const [enhancement, setEnhancement] = useState(true);

  // NEW: Image count, aspect ratio, resolution for recreate/variation
  const [numImages, setNumImages] = useState(1);
  const [ratio, setRatio] = useState('1:1');
  const [imgRes, setImgRes] = useState('1k');

  const srcRef = useRef();
  const cdRef = useRef();
  const genLockRef = useRef(false);

  const currentModel = IMG_MODELS.find(m => m.id === model);
  const showResolution = currentModel?.resolutions;

  // Cost calculation
  const getUpscaleCost = () => {
    const res = UPSCALE_RES.find(r => r.id === upscaleRes);
    return res ? res.cost : 3;
  };
  const resMult = IMG_RES.find(r => r.id === imgRes)?.mult || 1;
  const baseCost = mode === 'upscale' ? getUpscaleCost() : (currentModel?.cost || 3);
  const cost = mode === 'upscale' ? baseCost : Math.ceil(baseCost * resMult * numImages);

  const localJobs = pendingJobs.filter(j => j.type === 'edit');

  const editTypeMap = { recreate: 'edit-prompt', upscale: 'upscale', variation: 'edit-prompt' };

  const startCD = (s) => {
    setCd(s);
    if (cdRef.current) clearInterval(cdRef.current);
    cdRef.current = setInterval(() => {
      setCd(p => { if (p <= 1) { clearInterval(cdRef.current); return 0; } return p - 1; });
    }, 1000);
  };

  useEffect(() => {
    return () => { if (cdRef.current) clearInterval(cdRef.current); };
  }, []);

  const handleGen = useCallback(async () => {
    if (genLockRef.current) return;
    if (!user) { if (onShowAuth) onShowAuth(); return; }
    if (cd > 0) { toast.error(`Wait ${cd}s`); return; }
    if (!prompt.trim() && mode !== 'upscale') { toast.error('Enter a prompt'); return; }
    if (credits < cost) { toast.error(`Need ${cost} credits`); navigate('/subscription'); return; }
    if (!sourceImg && !sourceUrl) { toast.error('Upload or select a source image'); return; }
    genLockRef.current = true;

    // Create pending job placeholders
    const count = mode === 'upscale' ? 1 : numImages;
    const jobIds = Array.from({ length: count }, (_, i) => `epend_${Date.now()}_${i}`);
    const newJobs = jobIds.map(id => ({
      id, prompt: prompt.substring(0, 40), model: currentModel?.label || model, type: 'edit', mode,
    }));
    setPendingJobs(prev => [...newJobs, ...prev]);
    setLoading(true);
    startCD(8);

    // Fire generation in background
    (async () => {
      try {
        let editPrompt = prompt;
        if (mode === 'variation') {
          editPrompt = `Create a variation of: ${prompt}. Keep the same style and subject but with creative differences.`;
        } else if (mode === 'upscale') {
          editPrompt = `Upscale this image to ${upscaleRes.toUpperCase()} resolution with ${enhancement ? 'enhanced details, sharpness, and clarity' : 'standard quality'}.`;
        }

          let imageToSend = sourceImg?.fileUrl || sourceImg;
        if (!imageToSend && sourceUrl) {
          try {
            const resp = await fetch(sourceUrl);
            const blob = await resp.blob();
            imageToSend = new File([blob], 'source.png', { type: blob.type || 'image/png' });
          } catch {
            toast.error('Failed to load source image');
            setPendingJobs(prev => prev.filter(j => !jobIds.includes(j.id)));
            setLoading(false);
            return;
          }
        }

        const allResults = [];
        for (let i = 0; i < count; i++) {
          try {
            const res = await api.generateEdit(token, {
              editType: editTypeMap[mode],
              prompt: editPrompt,
              sourceImage: imageToSend,
              model: mode !== 'upscale' ? model : undefined,
              upscaleResolution: mode === 'upscale' ? upscaleRes : undefined,
              enhancement: mode === 'upscale' ? enhancement : undefined,
              aspectRatio: mode !== 'upscale' ? ratio : undefined,
              imageResolution: mode !== 'upscale' ? imgRes : undefined,
            });
            if (res.success) {
              allResults.push(res.imageUrl);
              setCredits(res.creditsRemaining);
            }
          } catch (e) {
            const msg = e.response?.data?.error;
            if (e.response?.status === 402) { toast.error('Not enough credits'); break; }
            else if (i === 0) toast.error(msg || 'Edit failed. Credits refunded.');
          }
        }

        setPendingJobs(prev => prev.filter(j => !jobIds.includes(j.id)));
        if (allResults.length > 0) {
          setResults(allResults);
          toast.success(`${allResults.length} image${allResults.length > 1 ? 's' : ''} generated!`);
          refreshItems(true);
        }
      } catch (e) {
        setPendingJobs(prev => prev.filter(j => !jobIds.includes(j.id)));
        toast.error('Edit failed');
      } finally {
        genLockRef.current = false;
        setLoading(false);
      }
    })();
  }, [prompt, mode, model, sourceImg, sourceUrl, cost, credits, cd, token, user, upscaleRes, enhancement, numImages, ratio, imgRes, navigate, setCredits, refreshItems, onShowAuth, currentModel]);

  const toggleFullscreen = (idx = 0) => { setFsIdx(idx); setFullscreen(!fullscreen); };
  const result = results[0] || null;
  const goResultEdit = () => navigate('/edit', { state: { sourceUrl: result, prompt, mode: 'recreate', fromLightbox: true } });
  const goResultRecreate = () => navigate('/edit', { state: { sourceUrl: result, prompt, mode: 'recreate', fromLightbox: true } });
  const goResultUpscale = () => navigate('/edit', { state: { sourceUrl: result, prompt, mode: 'upscale', fromLightbox: true } });
  const goResultVariation = () => navigate('/edit', { state: { sourceUrl: result, prompt, mode: 'variation', fromLightbox: true } });
  const goResultToVideo = () => navigate('/video', { state: { sourceUrl: result, prompt } });

  const modeLabel = { recreate: 'Recreate Image', upscale: 'Upscale Image', variation: 'Create Variation' };
  const modeIcon = { recreate: sz => <Icons.Recreate sz={sz||14} />, upscale: sz => <Icons.Upscale sz={sz||14} />, variation: sz => <Icons.Variation sz={sz||14} /> };

  return (
    <>
      {fullscreen && results[fsIdx] && (
        <div className="img-fs" onClick={() => setFullscreen(false)}>
          <img src={results[fsIdx]} alt="" className="img-fs-img" onClick={e => e.stopPropagation()} />
          <button className="img-fs-close" onClick={() => setFullscreen(false)}><Icons.X sz={16} /></button>
        </div>
      )}

      {/* Model Picker Popup */}
      {showModelPicker && (
        <div className="mp-overlay" onClick={() => setShowModelPicker(false)}>
          <div className="mp-popup" onClick={e => e.stopPropagation()}>
            <div className="mp-header">
              <div className="mp-title">Select Model</div>
              <button className="mc-btn" onClick={() => setShowModelPicker(false)}><Icons.X sz={14} /></button>
            </div>
            <div className="mp-body">
              {IMG_MODELS.map(m => (
                <button key={m.id} className={`mp-item ${model === m.id ? 'on' : ''}`}
                  onClick={() => { setModel(m.id); setShowModelPicker(false); }}>
                  {m.icon && Icons[m.icon] ? (
                    <div style={{ display:'flex', alignItems:'center', justifyContent:'center', width: 20, height: 20, marginRight: 10, color: m.color }}>
                      {React.createElement(Icons[m.icon], { sz: 18, color: m.color })}
                    </div>
                  ) : <div className="mp-dot" style={{ background: m.color }} />}
                  <div className="mp-info">
                    <div className="mp-name">{m.label}</div>
                  </div>
                  <div className="mp-badges">
                    {m.popular && <span className="mp-badge pop">Popular</span>}
                    {m.tag && <span className="mp-badge tag">{m.tag}</span>}
                  </div>
                  {model === m.id && (
                    <div className="mp-check">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                    </div>
                  )}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Mobile FAB */}
      <button className={`mobile-fab ${showMobilePreview ? 'on' : ''}`} onClick={() => setShowMobilePreview(!showMobilePreview)}>
        <Icons.ImgFrame sz={22} />
        <span>{showMobilePreview ? 'Close' : 'Preview'}</span>
      </button>

      <section className="panel">
        <div className="phead"><Icons.Edit sz={16} /> Edit Image</div>

        {/* Mode Tabs */}
        <div className="psec">
          <div className="edit-mode-tabs">
            {['recreate', 'variation'].map(m => (
              <button key={m} className={`edit-mode-tab ${mode === m ? 'on' : ''}`} onClick={() => setMode(m)}>
                <span className="emt-icon">{modeIcon[m] ? modeIcon[m](14) : null}</span>
                <span className="emt-label">{modeLabel[m]}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Model Selector — ABOVE Source Image (for recreate & variation) */}
        {mode !== 'upscale' && (
          <div className="psec">
            <div className="slbl">Model</div>
            <button className="mp-trigger" onClick={() => setShowModelPicker(true)}>
              <div className="mp-trigger-left">
                {currentModel?.icon && Icons[currentModel.icon] ? (
                  <div style={{ display:'flex', alignItems:'center', justifyContent:'center', width: 22, height: 22, marginRight: 10, color: currentModel.color }}>
                    {React.createElement(Icons[currentModel.icon], { sz: 20, color: currentModel.color })}
                  </div>
                ) : <div className="mp-dot" style={{ background: currentModel?.color }} />}
                <div>
                  <div className="mp-trigger-name">{currentModel?.label}</div>
                </div>
              </div>
              <div className="mp-trigger-right">
                {currentModel?.popular && <span className="mp-badge pop">Popular</span>}
                {currentModel?.tag && <span className="mp-badge tag">{currentModel?.tag}</span>}
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M6 9l6 6 6-6"/></svg>
              </div>
            </button>
          </div>
        )}

        {/* Source Image */}
        <div className="psec">
          <div className="slbl">Source Image <span className="opt">— {sourceUrl ? 'from gallery' : 'upload'}</span></div>
          {sourceUrl && !sourceImg ? (
            <div className="edit-source-preview">
              <img src={sourceUrl} alt="" className="edit-src-img" />
              <div className="edit-src-actions">
                <button className="edit-src-clear" onClick={() => setSourceUrl(null)}><Icons.X sz={10} /> Remove</button>
                <button className="edit-src-clear" onClick={() => srcRef.current?.click()}><Icons.Upload sz={10} /> Replace</button>
              </div>
            </div>
          ) : (
            <div className={`uzone ${sourceImg ? 'on' : ''}`} onClick={() => setLibOpen(true)}
              onDragOver={e => e.preventDefault()}
              onDrop={async e => { 
                e.preventDefault();
                if (e.dataTransfer.files[0]) { 
                  setSourceImg(await compressImage(e.dataTransfer.files[0])); 
                  setSourceUrl(null); 
                } 
              }}>
              {sourceImg ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <div style={{ position: 'relative', display: 'inline-block', flexShrink: 0 }}>
                    <img src={sourceImg.fileUrl || URL.createObjectURL(sourceImg)} alt="" style={{ width: 48, height: 48, borderRadius: 8, objectFit: 'cover' }} />
                    <button onClick={(e) => { e.stopPropagation(); setSourceImg(null); }} style={{ position: 'absolute', top: -6, right: -6, width: 20, height: 20, borderRadius: '50%', background: 'rgba(239, 68, 68, 0.9)', border: 'none', color: '#fff', fontSize: 10, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>✕</button>
                  </div>
                  <span style={{ color: 'var(--t2)', fontSize: 12 }}>{sourceImg.name || 'Library Image'}</span>
                </div>
              ) : (
                <><div style={{ opacity: .4 }}><Icons.Upload /></div><div>Visual References</div></>
              )}
            </div>
          )}
          <input ref={srcRef} type="file" accept="image/*" style={{ display: 'none' }}
            onChange={async e => { 
              if (e.target.files[0]) { 
                setSourceImg(await compressImage(e.target.files[0])); 
                setSourceUrl(null); 
              } 
            }} />
        </div>

        {/* Aspect Ratio — recreate & variation */}
        {mode !== 'upscale' && (
          <div className="psec">
            <div className="slbl">Aspect Ratio</div>
            <AspectRatioSelector
              options={RATIOS}
              value={ratio}
              onChange={setRatio}
            />
          </div>
        )}

        {/* Resolution — recreate & variation */}
        {mode !== 'upscale' && (
          <div className="psec">
            <div className="slbl">Resolution</div>
            <div className="pills">
              {IMG_RES.map(r => (
                <button key={r.id} className={`pill ${imgRes === r.id ? 'on' : ''}`}
                  onClick={() => setImgRes(r.id)}>
                  {r.label} <span style={{ fontSize: 9, opacity: .6 }}>({r.desc})</span>
                  {r.mult > 1 && <span style={{ fontSize: 9, color: 'var(--or)' }}> ×{r.mult}</span>}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Image Count — recreate & variation */}
        {mode !== 'upscale' && (
          <div className="psec">
            <div className="slbl">Number of Images</div>
            <div className="num-selector">
              <button className="num-btn" onClick={() => setNumImages(Math.max(1, numImages - 1))} disabled={numImages <= 1}>−</button>
              <span className="num-val">{numImages}</span>
              <button className="num-btn" onClick={() => setNumImages(Math.min(8, numImages + 1))} disabled={numImages >= 8}>+</button>
              <span style={{ fontSize: 10, color: 'var(--t3)', marginLeft: 8 }}>max 8</span>
            </div>
          </div>
        )}

        {/* Upscale Options — Resolution + Enhancement (upscale mode only) */}
        {mode === 'upscale' && (
          <div className="psec">
            <div className="slbl">Upscale Resolution</div>
            <div className="pills" style={{ marginBottom: 14 }}>
              {UPSCALE_RES.map(r => (
                <button key={r.id} className={`pill ${upscaleRes === r.id ? 'on' : ''}`}
                  onClick={() => setUpscaleRes(r.id)}>
                  {r.label} <span style={{ fontSize: 9, opacity: .6 }}>({r.desc})</span>
                </button>
              ))}
            </div>

            <div className="trow">
              <span className="tlbl">✨ Enhancement</span>
              <div className={`tog ${enhancement ? 'on' : ''}`} onClick={() => setEnhancement(!enhancement)}><div className="togk" /></div>
            </div>
            <div style={{ fontSize: 10, color: 'var(--t3)' }}>
              {enhancement ? 'Enhanced details, sharpness & clarity' : 'Standard upscale only'}
            </div>
          </div>
        )}

        {/* Prompt — recreate & variation only (NOT upscale) */}
        {mode !== 'upscale' && (
          <div className="psec">
            <div className="slbl">Edit Prompt</div>
            <div className="pwrap">
              <textarea className="ptxt"
                placeholder={mode === 'variation'
                  ? 'Describe the variation you want — style changes, color shifts, mood adjustments…'
                  : 'Describe changes or keep the original prompt to regenerate with a different model…'}
                value={prompt} 
                onChange={e => {
                  setPrompt(e.target.value);
                  const target = e.target;
                  target.style.height = '0px';
                  target.style.height = target.scrollHeight + 'px';
                }} 
                maxLength={10000} 
                style={{ minHeight: '120px', fontSize: '13px', resize: 'none', overflowY: 'hidden', flex: 'none', display: 'block' }} />
              <div className="pbot"><span className="pcnt" style={{ fontSize: '10px' }}>{prompt.length}/10000</span></div>
            </div>
          </div>
        )}

        {/* Generate Button */}
        <div className="gen-sticky">
          <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--t2)', marginBottom: 12, display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 8, flexWrap: 'wrap', textAlign: 'center' }}>
            {mode === 'upscale' ? (
              <>
                <strong>{upscaleRes.toUpperCase()}</strong> · Enhancement: <strong>{enhancement ? 'ON' : 'OFF'}</strong>
              </>
            ) : (
              <>
                <strong style={{ color: currentModel?.color }}>{currentModel?.label}</strong>
                {numImages > 1 && <span>· <strong>{numImages} imgs</strong></span>}
                <span>· {ratio}</span>
                {showResolution && <span>· {imgRes.toUpperCase()}</span>}
              </>
            )}
          </div>
          <div className="cost-row">
            <span className="cost-l">Cost</span>
            <span className="cost-v">
              {cost} cr
              {mode !== 'upscale' && numImages > 1 && ` (${Math.ceil(baseCost * resMult)}×${numImages})`}
              {' → '}{Math.max(0, (credits || 0) - cost)} left
            </span>
          </div>
          <button className="gbtn" onClick={handleGen}
            disabled={loading || (user && credits < cost) || cd > 0}>
            {cd > 0 ? `Wait ${cd}s` :
              !user ? <>✨ Sign up to {modeLabel[mode]}</> :
              <><Icons.Wand sz={15} /> {modeLabel[mode]} {numImages > 1 && mode !== 'upscale' ? `(×${numImages})` : ''}</>}
          </button>
          {user && credits < cost && (
            <p style={{ textAlign: 'center', color: 'var(--rd)', fontSize: 11, marginTop: 6, cursor: 'pointer' }}
              onClick={() => navigate('/subscription')}>Insufficient credits — <u>upgrade</u></p>
          )}
        </div>
      </section>

      {/* Main Canvas */}
      <div className={`main ${showMobilePreview ? 'mobile-preview-open' : ''}`}>
        <div className="mtbar">
          <button className="mtab main-back-mobile" onClick={() => setShowMobilePreview(false)}>
            <Icons.X sz={13} /> Close
          </button>
          <div className="mtab on">{modeIcon[mode] ? modeIcon[mode](13) : null} {modeLabel[mode]}</div>
        </div>
        <div className="mbody">
          {results.length > 0 && (
            <div className="prev">
              <div className="prev-wrap">
                  <div className="prev-toolbar">
                    <div className="prev-tb-right" style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
<button className="prev-icon-btn" onClick={() => toggleFullscreen(0)} title="Fullscreen">
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M8 3H5a2 2 0 00-2 2v3m18 0V5a2 2 0 00-2-2h-3m0 18h3a2 2 0 002-2v-3M3 16v3a2 2 0 002 2h3"/></svg>
                      </button>
                      <button className="prev-icon-btn" onClick={goResultEdit} title="Edit"><Icons.Edit sz={15} /></button>
<button className="prev-icon-btn" onClick={() => downloadFile(result, token).catch(() => toast.error('Download failed'))} title="Download"><Icons.DL sz={15} /></button>
                      <button className="prev-icon-btn del" onClick={async () => {
                        const isConfirmed = await confirmAlert({
                          title: 'Clear Results?',
                          message: 'Are you sure you want to clear these results?'
                        });
                        if (isConfirmed) setResults([]);
                      }} title="Remove"><Icons.Trash sz={15} /></button>
                    </div>
                  </div>

                  {/* Grid of results */}
                  {results.length === 1 ? (
                    <img src={results[0]} alt="" />
                  ) : (
                    <div className="edit-results-grid">
                      {results.map((url, i) => (
                        <div key={i} className="edit-result-item" onClick={() => toggleFullscreen(i)}>
                          <img src={url} alt="" />
                          <div className="edit-result-num">{i + 1}</div>
                        </div>
                      ))}
                    </div>
                  )}


                </div>
              </div>
          )}
        </div>
      </div>

      <MediaLibraryModal
        open={libOpen}
        onClose={() => setLibOpen(false)}
        allowedTypes={['image']}
        maxSelect={1}
        maxTypeCounts={{ image: 1, video: 0, audio: 0 }}
        onSelect={(assets) => {
          setSourceImg(assets[0]);
          setSourceUrl(null);
        }}
      />
    </>
  );
}


